import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { ResourceType, type UpdateVideoClipRequest } from '@packages/global';

import { videoClipApi } from '../../../services/user';
import { useToast } from '../../../stores/toastStore';
import StoryboardForm from '../forms/StoryboardForm';
import { useClipCanvasContext } from './ClipCanvasContext';
import {
  type AppliedMediaNodeData,
  type ClipCanvasMediaTarget,
  type ClipCanvasNode,
  type ClipResourceGroupType,
} from './types';

interface ClipCanvasPropertiesPanelProps {
  node: ClipCanvasNode | null;
  onClose: () => void;
}

const MEDIA_TARGET_FIELD: Partial<Record<ClipCanvasMediaTarget, keyof UpdateVideoClipRequest>> = {
  start: 'startImage',
  end: 'endImage',
  storyboard: 'storyboardImage',
  video: 'videoUrl',
};

const RESOURCE_GROUP_LABELS: Record<ClipResourceGroupType, string> = {
  image: '画面',
  audio: '配音',
  video: '视频',
};

const RESOURCE_TYPE_LABELS: Record<ResourceType, string> = {
  [ResourceType.START_IMAGE]: '开始帧',
  [ResourceType.END_IMAGE]: '结束帧',
  [ResourceType.STORYBOARD_IMAGE]: '分镜图',
  [ResourceType.VIDEO]: '视频',
  [ResourceType.SCRIPT_AUDIO]: '配音',
  [ResourceType.SOUND_EFFECT_AUDIO]: '音效',
  [ResourceType.LIP_SYNC_VIDEO]: '对口型视频',
  [ResourceType.AUDIO_EDIT]: '音频编辑',
};

function getAppliedMediaTitle(target: ClipCanvasMediaTarget): string {
  if (target === 'start') return '开始帧';
  if (target === 'end') return '结束帧';
  if (target === 'storyboard') return '分镜图';
  if (target === 'audio') return '配音';
  if (target === 'effects') return '特效';
  return '视频';
}

function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="clip-canvas-property-field">
      <span className="clip-canvas-property-field__label">{label}</span>
      {children}
    </label>
  );
}

function ReadonlyRow({ label, value }: { label: string; value?: string | number | null }) {
  return (
    <div className="clip-canvas-property-row">
      <span className="clip-canvas-property-row__label">{label}</span>
      <span className="clip-canvas-property-row__value">{value == null || value === '' ? '-' : value}</span>
    </div>
  );
}

export default function ClipCanvasPropertiesPanel({ node, onClose }: ClipCanvasPropertiesPanelProps) {
  const ctx = useClipCanvasContext();
  const { success, error } = useToast();
  const [saving, setSaving] = useState(false);
  const [mediaUrl, setMediaUrl] = useState('');

  useEffect(() => {
    if (!node) return;
    if (node.data.kind === 'applied-media') {
      setMediaUrl(node.data.url || '');
    }
  }, [node]);

  const title = useMemo(() => {
    if (!node) return '属性';
    if (node.data.kind === 'clip-context') return '分镜编辑';
    if (node.data.kind === 'applied-media') return `${getAppliedMediaTitle(node.data.target)}属性`;
    if (node.data.kind === 'candidate-resource') return '候选资源属性';
    if (node.data.kind === 'form-generator') return `${node.data.title}属性`;
    return '节点属性';
  }, [node]);

  if (!node) return null;

  const saveAppliedMedia = async (data: AppliedMediaNodeData) => {
    try {
      setSaving(true);
      const field = MEDIA_TARGET_FIELD[data.target];
      if (!field) return;
      await videoClipApi.updateVideoClip(ctx.videoClip.id, { [field]: mediaUrl } as UpdateVideoClipRequest);
      success('媒体属性已保存');
      await ctx.refreshVideoClip?.(ctx.videoClip.id);
    } catch (err) {
      error('媒体属性保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <aside className="clip-canvas-property-panel nodrag nopan nowheel" aria-label="节点属性面板">
      <div className="clip-canvas-property-panel__header">
        <div>
          <div className="clip-canvas-property-panel__eyebrow">Properties</div>
          <h2 className="clip-canvas-property-panel__title">{title}</h2>
        </div>
        <button type="button" className="clip-canvas-property-panel__close" onClick={onClose} aria-label="关闭属性面板">
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="clip-canvas-property-panel__body">
        {node.data.kind === 'clip-context' && (
          <div className="clip-canvas-property-panel__embedded-form">
            <StoryboardForm
              videoClip={node.data.videoClip}
              refreshVideoClip={ctx.refreshVideoClip}
            />
          </div>
        )}

        {node.data.kind === 'applied-media' && (
          <div className="clip-canvas-property-form">
            <ReadonlyRow label="类型" value={node.data.mediaType === 'image' ? '图片' : node.data.mediaType === 'audio' ? '音频' : node.data.mediaType === 'effects' ? '特效' : '视频'} />
            <ReadonlyRow label="位置" value={getAppliedMediaTitle(node.data.target)} />
            {(node.data.mediaType === 'image' || node.data.mediaType === 'video') ? (
              <Field label="资源 URL">
                <textarea rows={5} value={mediaUrl} onChange={(e) => setMediaUrl(e.target.value)} />
              </Field>
            ) : (
              <p className="clip-canvas-property-panel__note">
                请点击节点上的“编辑”打开预览区对应弹窗进行编辑。
              </p>
            )}
          </div>
        )}

        {node.data.kind === 'candidate-resource' && (
          <div className="clip-canvas-property-form">
            <ReadonlyRow label="名称" value={node.data.label} />
            <ReadonlyRow label="分组" value={RESOURCE_GROUP_LABELS[node.data.groupType]} />
            <ReadonlyRow label="候选数量" value={node.data.resources.length} />
            {node.data.resources.length === 0 ? (
              <p className="clip-canvas-property-panel__note">当前没有候选资源。</p>
            ) : (
              <div className="clip-canvas-property-resource-list">
                {node.data.resources.map((resource) => (
                  <div key={resource.id} className="clip-canvas-property-resource-item">
                    <ReadonlyRow label="类型" value={RESOURCE_TYPE_LABELS[resource.resourceType]} />
                    <ReadonlyRow label="状态" value={resource.status} />
                    <ReadonlyRow label="资源 ID" value={resource.id} />
                    <ReadonlyRow
                      label="生成任务"
                      value={
                        resource.imageGenerationId
                        || resource.imageEditId
                        || resource.videoGenerationId
                        || resource.audioGenerationId
                        || '-'
                      }
                    />
                    {resource.resultUrl && (
                      <Field label="结果 URL">
                        <textarea rows={3} value={resource.resultUrl} readOnly />
                      </Field>
                    )}
                    {resource.errorMessage && (
                      <Field label="错误信息">
                        <textarea rows={3} value={resource.errorMessage} readOnly />
                      </Field>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {node.data.kind === 'form-generator' && (
          <div className="clip-canvas-property-form">
            <ReadonlyRow label="节点 ID" value={node.id} />
            <ReadonlyRow label="节点类型" value={node.data.title} />
            <p className="clip-canvas-property-panel__note">
              生成参数在画布节点内直接编辑。可从图片节点拖线注入参考图到支持图片输入的表单节点。
            </p>
          </div>
        )}
      </div>

      {node.data.kind === 'applied-media' && (node.data.mediaType === 'image' || node.data.mediaType === 'video') && (
        <div className="clip-canvas-property-panel__footer">
          <button
            type="button"
            className="clip-canvas-property-panel__save"
            disabled={saving}
            onClick={() => {
              if (node.data.kind === 'applied-media') {
                void saveAppliedMedia(node.data);
              }
            }}
          >
            <CheckIcon className="h-4 w-4" />
            {saving ? '保存中' : '保存属性'}
          </button>
        </div>
      )}
    </aside>
  );
}
